/**
 * Models API slice.
 * Leaf module (Perf 3.1.b/e Step 5). Pulls only the shared chokepoint from
 * ./client; never imports a sibling @/lib/api slice.
 */
import { fetchWithAuth, ApiError } from './client';

export const modelsApi = {
  search: async (query, page = 1) => {
    try {
      const data = await fetchWithAuth(`/json/models/search?q=${encodeURIComponent(query)}&page=${page}`);
      
      if (data.error) {
        throw new ApiError(data.message || 'Failed to search models', 400, data);
      }
      
      return {
        models: data.models,
        pagination: {
          current: data.pagination.current || data.pagination.page || 1,
          pages: data.pagination.pages || Math.ceil(data.pagination.total / data.pagination.perPage),
          total: data.pagination.total,
          perPage: data.pagination.perPage,
          hasMore: data.pagination.hasMore
        },
        query: data.query
      };
    } catch (error) {
      console.error('Error searching models:', error);
      throw error;
    }
  },

  getModelsByTag: async (slug, page = 1) => {
    try {
      const data = await fetchWithAuth(`/json/model-tags/${slug}/models?page=${page}`);
      
      if (data.error) {
        throw new ApiError(data.message || 'Failed to fetch models for tag', 400, data);
      }
      
      return {
        modelTag: data.modelTag,
        models: data.models,
        pagination: {
          current: data.pagination.current || data.pagination.page || 1,
          pages: data.pagination.pages || Math.ceil(data.pagination.total / data.pagination.perPage),
          total: data.pagination.total,
          perPage: data.pagination.perPage,
          hasMore: data.pagination.hasMore
        }
      };
    } catch (error) {
      console.error('Error fetching models by tag:', error);
      throw error;
    }
  },

  getAllModelTags: async () => {
    try {
      const data = await fetchWithAuth('/json/model-tags/all');
      
      if (data.error) {
        throw new ApiError(data.message || 'Failed to fetch model tags', 400, data);
      }
      
      return data.modelTags;
    } catch (error) {
      console.error('Error fetching model tags:', error);
      throw error;
    }
  },

  getModelTagBySlug: async (slug) => {
    try {
      const data = await fetchWithAuth(`/json/model-tags/${slug}`);
      
      if (data.error) {
        throw new ApiError(data.message || 'Failed to fetch model tag', 400, data);
      }
      
      return data.modelTag;
    } catch (error) {
      console.error('Error fetching model tag:', error);
      throw error;
    }
  },

  getModelById: async (modelId) => {
    try {
      const data = await fetchWithAuth(`/json/models/${modelId}`);
      
      if (data.error) {
        throw new ApiError(data.message || 'Failed to fetch model', 400, data);
      }
      
      return data.model;
    } catch (error) {
      console.error('Error fetching model:', error);
      throw error;
    }
  },

  // Fetch model by slug - uses the same endpoint which supports both ID and slug
  getModelBySlug: async (slug) => {
    try {
      const data = await fetchWithAuth(`/json/models/${slug}`);
      
      if (data.error) {
        throw new ApiError(data.message || 'Failed to fetch model by slug', 400, data);
      }
      
      return data.model;
    } catch (error) {
      console.error('Error fetching model by slug:', error);
      throw error;
    }
  },

  getDefaultOutputModels: async () => {
    try {
      const data = await fetchWithAuth('/json/models/default-output-models', {
        cache: 'no-store',
      });

      if (data.error) {
        throw new ApiError(data.message || 'Failed to fetch default output models', 400, data);
      }

      return data.defaults || {};
    } catch (error) {
      console.error('Error fetching default output models:', error);
      throw error;
    }
  },

  getFeaturedModels: async () => {
    try {
      const data = await fetchWithAuth('/json/models/featured');
      
      if (data.error) {
        throw new ApiError(data.message || 'Failed to fetch featured models', 400, data);
      }
      
      return data.models;
    } catch (error) {
      console.error('Error fetching featured models:', error);
      throw error;
    }
  },

  getFrequentModels: async () => {
    try {
      const data = await fetchWithAuth('/json/models/frequent');

      if (data.error) {
        throw new ApiError(data.message || 'Failed to fetch frequent models', 400, data);
      }

      return data.models;
    } catch (error) {
      console.error('Error fetching frequent models:', error);
      throw error;
    }
  },

  getAllModels: async (page = 1) => {
    try {
      const data = await fetchWithAuth(`/json/models?page=${page}`);
      
      if (data.error) {
        throw new ApiError(data.message || 'Failed to fetch all models', 400, data);
      }
      
      const current = data.pagination?.current || data.pagination?.page || 1;
      const pages = data.pagination?.pages || Math.ceil((data.pagination?.total || 0) / (data.pagination?.perPage || 20));
      const hasMore = typeof data.pagination?.hasMore === 'boolean'
        ? data.pagination.hasMore
        : current < pages;

      return {
        models: data.models,
        pagination: {
          current,
          pages,
          total: data.pagination?.total || 0,
          perPage: data.pagination?.perPage || 20,
          hasMore
        }
      };
    } catch (error) {
      console.error('Error fetching all models:', error);
      throw error;
    }
  },

  favoriteModel: async (modelId) => {
    try {
      const data = await fetchWithAuth(`/json/models/${modelId}/favorite`, {
        method: 'POST'
      });
      
      if (data.error) {
        throw new ApiError(data.message || 'Failed to favorite model', 400, data);
      }
      
      return !data.error;
    } catch (error) {
      console.error('Error favoriting model:', error);
      throw error;
    }
  },

  unfavoriteModel: async (modelId) => {
    try {
      const data = await fetchWithAuth(`/json/models/${modelId}/favorite`, {
        method: 'DELETE'
      });
      
      if (data.error) {
        throw new ApiError(data.message || 'Failed to unfavorite model', 400, data);
      }
      
      return !data.error;
    } catch (error) {
      console.error('Error unfavoriting model:', error);
      throw error;
    }
  }
};
